import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import DateFilterPopover from '@/components/routes/route-filters/date-filter-popover';
import RouteSorting from '@/components/routes/route-filters/route-sorting';
import RouteStatusFilter from '@/components/routes/route-filters/route-status-filter';
import PillButton from '@/components/shared/pill-button';
import { FILTER_SORT_BY, FILTER_STATUS } from '@/utils/constants';
import {
  SEARCH_PARAM_ROUTE_TYPE,
  SEARCH_PARAM_SELECTED_DATE,
  SEARCH_PARAM_SORT_BY,
} from '@/utils/constants/queries';
import formatDate from '@/utils/helpers/format-date';
import { getToday } from '@/utils/helpers/get-formatted-date-for-api';
import isSameDayAsToday from '@/utils/helpers/is-same-day-as-today';

const RouteFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const selectedDateStr = searchParams.get(SEARCH_PARAM_SELECTED_DATE);
  const selectedDate = useMemo(() => {
    return selectedDateStr || formatDate(getToday());
  }, [selectedDateStr]);

  const isParamDefault = (param: string | null, defaultValue: string) =>
    !param || param === defaultValue;

  const isToday = isSameDayAsToday(selectedDate);

  const isRouteStatusTypeDefault = isParamDefault(
    searchParams.get(SEARCH_PARAM_ROUTE_TYPE),
    FILTER_STATUS.ALL
  );

  const isRouteSortTypeDefault = isParamDefault(
    searchParams.get(SEARCH_PARAM_SORT_BY),
    FILTER_SORT_BY.DEVIATION
  );

  const isResetDisabled = isToday && isRouteStatusTypeDefault && isRouteSortTypeDefault;

  const onReset = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete(SEARCH_PARAM_ROUTE_TYPE);
      prev.delete(SEARCH_PARAM_SORT_BY);
      prev.delete(SEARCH_PARAM_SELECTED_DATE);

      return prev;
    });
  }, []);

  return (
    <div className="flex w-full gap-1 flex-wrap">
      <DateFilterPopover />
      <RouteStatusFilter isToday={isToday} />
      <RouteSorting isToday={isToday} />
      <PillButton onClick={onReset} isDisabled={isResetDisabled}>
        {t('filters.reset')}
      </PillButton>
    </div>
  );
};

export default RouteFilters;
